import { useQuery, useMutation, useQueryClient, keepPreviousData, type QueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { createPersistedMockData } from '../mockDataManager';
import {
  createGobodoObjectType,
  updateGobodoObjectType,
  deleteGobodoObjectType,
  getGobodoObjectTypes,
  getGobodoObjectTypesByCategory,
  getGobodoObjectTypesPageByCategory,
  getGobodoObjectTypesPageInGraph,
  getGobodoObjectTypeByKey,
  searchGobodoObjectTypes,
  resolveObjectKey
} from '../gobodo/gobodoObjectService';

import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { ensureError } from '../../utils/gobodoErrorHandler';

// ============================================
// TYPES
// ============================================

export interface CustomObject {
  id: string;
  gobodoTypeId?: number;
  label: string;
  apiName: string;
  pluralLabel: string;
  description: string;
  icon: string; // Lucide icon name
  isCustom: boolean;
  isActive: boolean;
  fieldCount: number;
  recordCount: number;
  lastModified: string;
  createdBy: string;
  category: 'core' | 'custom' | 'system';
}

export interface PaginatedObjectsResult {
  items: CustomObject[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isTotalExact: boolean;
}

// ============================================
// MOCK DATA
// ============================================

const defaultMockObjects: CustomObject[] = [
  // SALES Objects
  {
    id: 'obj_lead',
    label: 'Lead',
    apiName: 'Lead__c',
    pluralLabel: 'Leads',
    description: 'Potential customers and prospects',
    icon: 'Target',
    isCustom: false,
    isActive: true,
    fieldCount: 15,
    recordCount: 10,
    lastModified: '2025-01-15T10:00:00Z',
    createdBy: 'System',
    category: 'core'
  },
  {
    id: 'obj_client',
    label: 'Client',
    apiName: 'Client__c',
    pluralLabel: 'Clients',
    description: 'Companies and organizations that are clients',
    icon: 'Building',
    isCustom: false,
    isActive: true,
    fieldCount: 18,
    recordCount: 10,
    lastModified: '2025-01-14T15:30:00Z',
    createdBy: 'System',
    category: 'core'
  },
  {
    id: 'obj_opportunity',
    label: 'Opportunity',
    apiName: 'Opportunity__c',
    pluralLabel: 'Opportunities',
    description: 'Sales deals and pipeline opportunities',
    icon: 'TrendingUp',
    isCustom: false,
    isActive: true,
    fieldCount: 16,
    recordCount: 10,
    lastModified: '2025-01-13T09:15:00Z',
    createdBy: 'System',
    category: 'core'
  },

  // SERVICE Objects
  {
    id: 'obj_contact',
    label: 'Contact',
    apiName: 'Contact__c',
    pluralLabel: 'Contacts',
    description: 'Individual contacts associated with clients',
    icon: 'User',
    isCustom: false,
    isActive: true,
    fieldCount: 14,
    recordCount: 12,
    lastModified: '2025-01-12T11:20:00Z',
    createdBy: 'System',
    category: 'core'
  },
  {
    id: 'obj_case',
    label: 'Case',
    apiName: 'Case__c',
    pluralLabel: 'Cases',
    description: 'Customer service cases and support tickets',
    icon: 'Ticket',
    isCustom: false,
    isActive: true,
    fieldCount: 18,
    recordCount: 15,
    lastModified: '2025-01-11T14:45:00Z',
    createdBy: 'System',
    category: 'core'
  },

  // PRODUCTIVITY Objects
  {
    id: 'obj_event',
    label: 'Event',
    apiName: 'Event__c',
    pluralLabel: 'Events',
    description: 'Meetings, calls, and scheduled activities',
    icon: 'Calendar',
    isCustom: false,
    isActive: true,
    fieldCount: 12,
    recordCount: 18,
    lastModified: '2025-01-10T16:30:00Z',
    createdBy: 'System',
    category: 'core'
  },

  // ANALYTICS Objects
  {
    id: 'obj_report',
    label: 'Report',
    apiName: 'Report__c',
    pluralLabel: 'Reports',
    description: 'Saved reports and analytics',
    icon: 'FileText',
    isCustom: true,
    isActive: true,
    fieldCount: 8,
    recordCount: 12,
    lastModified: '2025-01-09T10:00:00Z',
    createdBy: 'Admin',
    category: 'custom'
  },

  // SYSTEM Objects
  {
    id: 'obj_user',
    label: 'User',
    apiName: 'User__c',
    pluralLabel: 'Users',
    description: 'System users and team members',
    icon: 'Users',
    isCustom: false,
    isActive: true,
    fieldCount: 12,
    recordCount: 24,
    lastModified: '2025-01-08T13:00:00Z',
    createdBy: 'System',
    category: 'system'
  },

  // CYBERSWEEP Objects
  {
    id: 'obj_cs_audit',
    label: 'CS Audit',
    apiName: 'CSAudit__c',
    pluralLabel: 'CS Audits',
    description: 'CyberSweep audit records tracking website PII audits for data broker monitoring',
    icon: 'ShieldCheck',
    isCustom: true,
    isActive: true,
    fieldCount: 14,
    recordCount: 25,
    lastModified: '2025-12-06T12:00:00Z',
    createdBy: 'System',
    category: 'custom'
  },
  {
    id: 'obj_paw_record',
    label: 'PAW Record',
    apiName: 'PAWRecord__c',
    pluralLabel: 'PAW Records',
    description: 'Performed Audit Website records tracking individual website audits within CyberSweep',
    icon: 'Eye',
    isCustom: true,
    isActive: true,
    fieldCount: 16,
    recordCount: 150,
    lastModified: '2025-12-06T12:00:00Z',
    createdBy: 'System',
    category: 'custom'
  },

  // ACTIVITY Object (for Gobodo integration)
  {
    id: 'obj_activity',
    label: 'Activity',
    apiName: 'Activity__c',
    pluralLabel: 'Activities',
    description: 'Track all activities (calls, meetings, tasks, notes) across records',
    icon: 'Activity',
    isCustom: true,
    isActive: true,
    fieldCount: 18,
    recordCount: 50,
    lastModified: '2026-02-26T12:00:00Z',
    createdBy: 'System',
    category: 'custom'
  }
];

// Create persisted objects using centralized mock data manager
const persistedObjects = createPersistedMockData('crm_objects', defaultMockObjects);

// Export for backwards compatibility
export const mockObjects = persistedObjects.data;

// ============================================
// QUERY KEYS
// ============================================

export const objectKeys = {
  all: ['objects'] as const,
  lists: () => [...objectKeys.all, 'list'] as const,
  list: (filters: string) => [...objectKeys.lists(), filters] as const,
  pagedLists: () => [...objectKeys.all, 'paged-list'] as const,
  pagedList: (filters: string, page: number, pageSize: number) => [...objectKeys.pagedLists(), filters, page, pageSize] as const,
  details: () => [...objectKeys.all, 'detail'] as const,
  detail: (id: string) => [...objectKeys.details(), id] as const,
};

function isCustomObject(value: unknown): value is CustomObject {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<CustomObject>;
  return typeof candidate.id === 'string' && typeof candidate.apiName === 'string';
}

function extractObjectsFromQueryData(data: unknown): CustomObject[] {
  if (Array.isArray(data)) {
    return data.filter(isCustomObject);
  }

  if (data && typeof data === 'object') {
    const record = data as Partial<PaginatedObjectsResult & CustomObject>;

    if (Array.isArray(record.items)) {
      return record.items.filter(isCustomObject);
    }

    if (isCustomObject(record)) {
      return [record];
    }
  }

  return [];
}

export function findCachedObjectById(queryClient: QueryClient, objectId: string): CustomObject | undefined {
  const queryCache = queryClient.getQueryCache();
  const allObjectQueries = queryCache.findAll({ queryKey: objectKeys.all });

  for (const query of allObjectQueries) {
    const objects = extractObjectsFromQueryData(query.state.data);
    const cachedObject = objects.find(obj => obj.id === objectId);
    if (cachedObject) return cachedObject;
  }

  return undefined;
}

function applyObjectFilters(
  objects: CustomObject[],
  filters?: {
    search?: string;
    category?: 'core' | 'custom' | 'system' | 'all';
    isActive?: boolean;
  }
): CustomObject[] {
  let filtered = objects;

  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(obj =>
      obj.label.toLowerCase().includes(searchLower) ||
      obj.apiName.toLowerCase().includes(searchLower) ||
      obj.description.toLowerCase().includes(searchLower)
    );
  }

  if (filters?.category && filters.category !== 'all') {
    filtered = filtered.filter(obj => obj.category === filters.category);
  }

  if (filters?.isActive !== undefined) {
    filtered = filtered.filter(obj => obj.isActive === filters.isActive);
  }

  return filtered;
}

function paginateObjects(
  objects: CustomObject[],
  page: number,
  pageSize: number
): PaginatedObjectsResult {
  const safePage = Math.max(page, 1);
  const safePageSize = Math.max(pageSize, 1);
  const totalRecords = objects.length;
  const totalPages = totalRecords === 0 ? 1 : Math.ceil(totalRecords / safePageSize);
  const normalizedPage = Math.min(safePage, totalPages);
  const start = (normalizedPage - 1) * safePageSize;
  const end = start + safePageSize;

  return {
    items: objects.slice(start, end),
    page: normalizedPage,
    pageSize: safePageSize,
    totalRecords,
    totalPages,
    hasNextPage: normalizedPage < totalPages,
    hasPreviousPage: normalizedPage > 1,
    isTotalExact: true
  };
}

// ============================================
// HOOKS
// ============================================

/**
 * Get all objects with optional filtering
 * Future: Replace with API call to GET /api/objects
 */
export function useObjects(
  filters?: {
    search?: string;
    category?: 'core' | 'custom' | 'system' | 'all';
    isActive?: boolean;
  },
  queryOptions?: Pick<UseQueryOptions<CustomObject[]>, 'enabled'>
) {
  return useQuery({
    ...queryOptions,
    queryKey: objectKeys.list(JSON.stringify(filters || {})),
    queryFn: async () => {
      try {
      if (USE_GOBODO_API) {
        let objects: CustomObject[];

        // If a specific category is selected, fetch only from that schema
        if (filters?.category && filters.category !== 'all') {
          objects = await getGobodoObjectTypesByCategory(filters.category);
        } else {
          // Fetch from all schemas when 'all' is selected or no category filter
          objects = await getGobodoObjectTypes();
        }

        return applyObjectFilters(objects, filters);
      } else {
        // Fallback to mock data
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 400));

        return applyObjectFilters(persistedObjects.get(), filters);
      }
      } catch (e) {
        throw ensureError(e);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function usePagedObjects(
  filters?: {
    search?: string;
    category?: 'core' | 'custom' | 'system' | 'all';
    isActive?: boolean;
  },
  pagination?: {
    page?: number;
    pageSize?: number;
  },
  queryOptions?: Pick<UseQueryOptions<PaginatedObjectsResult>, 'enabled'>
) {
  const page = Math.max(pagination?.page ?? 1, 1);
  const pageSize = Math.max(pagination?.pageSize ?? 25, 1);

  return useQuery({
    ...queryOptions,
    queryKey: objectKeys.pagedList(JSON.stringify(filters || {}), page, pageSize),
    queryFn: async () => {
      try {
        if (USE_GOBODO_API) {
          const selectedCategory = filters?.category;
          const normalizedSearch = filters?.search?.trim();
          const canUseSchemaPagination =
            !normalizedSearch &&
            (filters?.isActive === undefined || filters.isActive === true) &&
            !!selectedCategory &&
            selectedCategory !== 'all';
          const canUseGraphPagination =
            !normalizedSearch &&
            (filters?.isActive === undefined || filters.isActive === true) &&
            (!selectedCategory || selectedCategory === 'all');

          if (normalizedSearch) {
            return await searchGobodoObjectTypes(normalizedSearch, {
              page: page - 1,
              size: Math.min(pageSize, 100),
              category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : undefined
            });
          }

          if (canUseSchemaPagination && selectedCategory) {
            return await getGobodoObjectTypesPageByCategory(selectedCategory, {
              page: page - 1,
              size: Math.min(pageSize, 100)
            });
          }

          if (canUseGraphPagination) {
            return await getGobodoObjectTypesPageInGraph({
              page: page - 1,
              size: Math.min(pageSize, 100)
            });
          }

          const objects =
            selectedCategory && selectedCategory !== 'all'
              ? await getGobodoObjectTypesByCategory(selectedCategory)
              : await getGobodoObjectTypes();

          return paginateObjects(applyObjectFilters(objects, filters), page, pageSize);
        }

        await new Promise(resolve => setTimeout(resolve, 400));
        return paginateObjects(
          applyObjectFilters(persistedObjects.get(), filters),
          page,
          pageSize
        );
      } catch (e) {
        throw ensureError(e);
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get single object by ID
 * Future: Replace with API call to GET /api/objects/:id
 */
export function useObject(objectId: string) {
  const queryClient = useQueryClient();
  
  return useQuery<CustomObject>({
    queryKey: objectKeys.detail(objectId),
    queryFn: async () => {
      try {
      if (USE_GOBODO_API) {
        // Try to get the exact apiName from the objects cache so Gobodo lookup
        // reuses the backend key format from whichever object query loaded first.
        const cachedObject = findCachedObjectById(queryClient, objectId);
        
        const exactKey = cachedObject?.apiName;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('[useObject] Resolving key:', {
            objectId,
            foundInCache: !!cachedObject,
            cachedObject: cachedObject ? { id: cachedObject.id, apiName: cachedObject.apiName } : null,
            exactKey,
            resolvedKey: resolveObjectKey(objectId, exactKey)
          });
        }
        
        // Use resolveObjectKey with the exact apiName if available
        const key = resolveObjectKey(objectId, exactKey);
        
        // If we didn't find the cached object, log a warning
        if (!cachedObject && process.env.NODE_ENV === 'development') {
          const allObjectQueries = queryClient.getQueryCache().findAll({ queryKey: objectKeys.all });
          const firstQueryObjects = extractObjectsFromQueryData(allObjectQueries[0]?.state.data);
          const sampleIds = firstQueryObjects.length > 0
            ? [{ id: firstQueryObjects[0].id, apiName: firstQueryObjects[0].apiName }]
            : [];
          
          console.warn('[useObject] Could not find object in cache:', {
            objectId,
            availableQueries: allObjectQueries.length,
            sampleIds
          });
        }
        
        return await getGobodoObjectTypeByKey(key);
      } else {
        // Fallback to mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        const object = persistedObjects.get().find(obj => obj.id === objectId);
        if (!object) throw new Error('Object not found');
        return object;
      }
      } catch (e: unknown) {
        throw ensureError(e);
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!objectId,
  });
}

/**
 * Create new custom object
 * Future: Replace with API call to POST /api/objects
 */
export function useCreateObject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (objectData: Omit<CustomObject, 'id' | 'fieldCount' | 'recordCount' | 'lastModified' | 'createdBy'>) => {
      if (USE_GOBODO_API) {
        // Ensure category defaults to 'custom' if not specified
        const objectDataWithCategory = {
          ...objectData,
          category: objectData.category || 'custom'
        };
        return createGobodoObjectType(objectDataWithCategory);
      } else {
        // Fallback to mock data
        await new Promise(resolve => setTimeout(resolve, 500));

        const newObject: CustomObject = {
          ...objectData,
          id: `obj_${Date.now()}`,
          fieldCount: 0,
          recordCount: 0,
          lastModified: new Date().toISOString(),
          createdBy: 'Current User',
          isCustom: true,
          category: 'custom'
        };

        return persistedObjects.add(newObject);
      }
    },
    onSuccess: (createdObject) => {
      queryClient.invalidateQueries({ queryKey: objectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.refetchQueries({ queryKey: objectKeys.lists() });
      if (createdObject?.id) {
        queryClient.setQueryData(objectKeys.detail(createdObject.id), createdObject);
      }
    },
  });
}

/**
 * Update object
 */
export function useUpdateObject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ objectId, updates }: { objectId: string; updates: Partial<CustomObject> }) => {
      if (USE_GOBODO_API) {
        return updateGobodoObjectType(objectId, updates);
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      const finalUpdates = { ...updates, lastModified: new Date().toISOString() };
      const updated = persistedObjects.update(objectId, finalUpdates);
      if (!updated) throw new Error('Object not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: objectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: objectKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.refetchQueries({ queryKey: objectKeys.lists() });
    },
  });
}

/**
 * Delete object (deactivate)
 */
export function useDeleteObject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (objectId: string) => {
      if (USE_GOBODO_API) {
        await deleteGobodoObjectType(objectId);
        return { id: objectId, isActive: false } as CustomObject;
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      const updated = persistedObjects.update(objectId, {
        isActive: false,
        lastModified: new Date().toISOString()
      });
      if (!updated) throw new Error('Object not found');
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.refetchQueries({ queryKey: objectKeys.lists() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format relative time for last modified
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

/**
 * Get category badge color
 */
export function getCategoryColor(category: CustomObject['category']) {
  switch (category) {
    case 'core': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'custom': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'system': return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
}
